import React, {useEffect} from 'react';
import {Dimensions, View} from 'react-native';
import Modal from 'react-native-modal';
import {Avatar, CheckBox, Icon, ListItem, makeStyles} from '@rneui/themed';
import {Formik} from 'formik';

import {useStores} from '../../../stores/use-stores';
import Over18 from '../../Over18';
import Button from '../../themed/Button';
import Text from '../../themed/Text';
import TextInput from '../../themed/TextInput';
import PressableWithOpacityFeedback from '../../ui-controls/PressableWithOpacityFeedback';

import {AcceptOrderSchema} from './validationSchema';

function PersonAcceptingTheOrder() {
  const styles = useStyles();
  const [isVisible, setIsVisible] = React.useState(false);
  const {cartStore, userStore} = useStores();

  useEffect(() => {
    if (!cartStore.willBePresent && !cartStore.nominatedPersonName) {
      cartStore.setWillBePresent(true);
    }
  }, [cartStore.willBePresent, cartStore.nominatedPersonName, cartStore]);

  const onClose = () => {
    setIsVisible(false);
  };
  const onOpen = () => setIsVisible(true);
  return (
    <>
      <ListItem onPress={onOpen}>
        <Avatar
          icon={{
            type: 'material',
            name: 'person-outline',
            size: 24,
            color: 'black',
          }}
        />
        <ListItem.Content>
          <ListItem.Subtitle>Person accepting the order</ListItem.Subtitle>
          <ListItem.Title>
            <Text variant="body2">
              {cartStore.hasNominatedPerson
                ? cartStore.nominatedPersonName
                    .split('\t')
                    .filter((_, i) => i < 2)
                    .join(' ') // just return first and last name from `first\tlast\tmobile`
                : userStore.name}
            </Text>
          </ListItem.Title>
        </ListItem.Content>
        <ListItem.Chevron size={24} color={styles.text.color} />
      </ListItem>
      <Modal
        isVisible={isVisible}
        hasBackdrop
        backdropOpacity={0.75}
        onBackdropPress={onClose}
        onBackButtonPress={onClose}>
        <View
          style={{
            maxHeight: Dimensions.get('window').height,
            backgroundColor: 'white',
          }}>
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingHorizontal: 20,
            }}>
            <Text style={{marginTop: 24, marginBottom: 18}} variant="heading1">
              Who will accept the order?
            </Text>
            <PressableWithOpacityFeedback onPress={onClose}>
              <Icon name="close" size={30} />
            </PressableWithOpacityFeedback>
          </View>
          <Formik
            validationSchema={AcceptOrderSchema}
            initialValues={{
              firstName: cartStore.nominatedPersonParts?.firstName,
              lastName: cartStore.nominatedPersonParts?.lastName,
              mobile: cartStore.nominatedPersonParts?.mobile,
              person: cartStore.hasNominatedPerson ? 'someone_else' : 'me',
            }}
            onSubmit={(
              {firstName, lastName, mobile, person},
              {setSubmitting},
            ) => {
              setSubmitting(true);

              if (person === 'someone_else') {
                cartStore.setNominatedPersonParts({
                  firstName,
                  lastName,
                  mobile,
                });
                cartStore.setWillBePresent(false);
                cartStore.setHasNominatedPerson(true);
              } else if (person === 'me') {
                cartStore.setWillBePresent(true);
                cartStore.setHasNominatedPerson(false);
              }

              onClose?.();
              setSubmitting(false);
            }}>
            {({
              initialValues,
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
              errors,
              touched,
            }) => {
              return (
                <View
                  style={{
                    paddingTop: 10,
                    paddingHorizontal: 20,
                  }}>
                  <View style={{flexDirection: 'row'}}>
                    <CheckBox
                      checked={values?.person === 'me'}
                      onPress={() => setFieldValue('person', 'me')}
                      iconType="material-community"
                      checkedIcon="radiobox-marked"
                      uncheckedIcon="radiobox-blank"
                      title={'Me'}
                      textStyle={{fontWeight: 'normal'}}
                    />
                    <CheckBox
                      checked={values?.person === 'someone_else'}
                      onPress={() => setFieldValue('person', 'someone_else')}
                      iconType="material-community"
                      checkedIcon="radiobox-marked"
                      uncheckedIcon="radiobox-blank"
                      title={'Someone else'}
                      textStyle={{fontWeight: 'normal'}}
                    />
                  </View>

                  {values?.person === 'me' ? (
                    <View>
                      <ListItem>
                        <ListItem.Content>
                          <ListItem.Subtitle>First name</ListItem.Subtitle>
                          <ListItem.Title>{userStore.firstName}</ListItem.Title>
                        </ListItem.Content>
                      </ListItem>
                      <ListItem>
                        <ListItem.Content>
                          <ListItem.Subtitle>Last name</ListItem.Subtitle>
                          <ListItem.Title>{userStore.lastName}</ListItem.Title>
                        </ListItem.Content>
                      </ListItem>
                      <ListItem>
                        <ListItem.Content>
                          <ListItem.Subtitle>Mobile number</ListItem.Subtitle>
                          <ListItem.Title>
                            {userStore.phoneNumber}
                          </ListItem.Title>
                        </ListItem.Content>
                      </ListItem>
                    </View>
                  ) : (
                    <View style={{gap: 8}}>
                      <TextInput
                        label="First name"
                        defaultValue={initialValues.firstName}
                        onChange={handleChange('firstName')}
                        onBlur={handleBlur('firstName')}
                        errorMessage={touched.firstName ? errors.firstName : ''}
                      />
                      <TextInput
                        label="Last name"
                        defaultValue={initialValues.lastName}
                        onChange={handleChange('lastName')}
                        onBlur={handleBlur('lastName')}
                        errorMessage={touched.lastName ? errors.lastName : ''}
                      />
                      <TextInput
                        label="Mobile number"
                        defaultValue={initialValues.mobile}
                        onChange={handleChange('mobile')}
                        onBlur={handleBlur('mobile')}
                        errorMessage={touched.mobile ? errors.mobile : ''}
                      />
                    </View>
                  )}

                  <View style={{paddingVertical: 20}}>
                    <Button
                      loading={isSubmitting}
                      disabled={isSubmitting}
                      onPress={handleSubmit}>
                      SAVE
                    </Button>
                  </View>

                  <Over18 />
                </View>
              );
            }}
          </Formik>
        </View>
      </Modal>
    </>
  );
}

const useStyles = makeStyles(theme => {
  return {
    text: {
      color: theme.colors?.legacy?.text?.heading?.primary,
    },
  };
});

export default PersonAcceptingTheOrder;

// const WhoWillAcceptTheOrderModal = () =>{
//     return <Modal  isVisible>

//     </Modal>
// }
